import {
  Form,
  Input,
  Radio,
  Select,
  Button,
  Checkbox,
  DatePicker,
  Table,
  TreeSelect,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { CloseOutlined, SearchOutlined } from "@ant-design/icons";
import { ChildrenInfoType, BranchInfoType } from "./types";
import fetchAdvisorsData from "./utils/fetchAdvisorsData";
import { convertToAdvisorTreeSelectOptions } from "./utils/convertToAdvisorTreeSelectOptions";
import { MediaCodeDataType } from "../systemManagement/types";
import { header } from "../utils/salesAdminHeader";
import { apiCall } from "../../../utils/apiCall";
import requiredRule from "../../../utils/formRules/requiredRule";

interface MediaIdOptionsType {
  localMarketingId?: number;
  categoryId?: number;
  code: string;
  name: string;
}

const SingleLeadCreation = forwardRef((props, ref) => {
  const [showAddressWindow, setShowAddressWindow] = useState(false);
  const [mediaList, setMediaList] = useState<MediaIdOptionsType[]>();
  const [mediaFullList, setMediaFullList] = useState({
    CL: [],
    DLBD: [],
    DLGD: [],
    DLLD: [],
  });
  const [isPregnant, setIsPregnant] = useState(false);
  const [dueDate, setDueDate] = useState<string | null>("");
  const [childInfo, setChildInfo] = useState<ChildrenInfoType[]>([
    { name: "", birthday: "" },
    { name: "", birthday: "" },
  ]);
  const [childInfoMissing, setChildInfoMissing] = useState(false);
  const [advisors, setAdvisors] = useState<BranchInfoType[]>([]);
  const [advisorsTreeSelectOptions, setAdvisorsTreeSelectOptions] =
    useState<any>([]);
  const [clDl, setClDl] = useState("");
  const [leadType, setLeadType] = useState("");
  const [addressInfo, setAddressInfo] = useState({
    SGG_CD: "",
    ROAD_CD: "",
    EMD_CD: "",
    BULD_NO: "",
  });
  const [form] = Form.useForm();
  const modalRef = useRef<HTMLDivElement>(null);

  const fetchMedia = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + "/leads/admin/options/media",
    });
    if (data) setMediaFullList(data);
  };

  const columns: ColumnsType<ChildrenInfoType> = [
    {
      title: "",
      render: (_, __, index) => (index !== 1 ? index + 1 : ""),
    },
    {
      title: "자녀이름",
      dataIndex: "name",
      key: "name",
      render: (name, _, index) => {
        return index !== 1 ? (
          <Input
            defaultValue={name}
            placeholder="자녀이름"
            onChange={(e) => {
              const replica = JSON.parse(JSON.stringify(childInfo));
              replica[index].name = e.target.value;
              setChildInfo(replica);
            }}
          />
        ) : (
          <Checkbox
            defaultChecked={isPregnant}
            onChange={(e) => {
              setIsPregnant(e.target.checked);
            }}
          >
            현재 임신중입니다.
          </Checkbox>
        );
      },
    },
    {
      title: "자녀생일",
      dataIndex: "birthday",
      key: "birthday",
      render: (day, _, index) => {
        return (
          <DatePicker
            placeholder={`${index !== 1 ? "생년월일" : "출산예정일"}`}
            onChange={(e) => {
              if (index !== 1) {
                const replica = JSON.parse(JSON.stringify(childInfo));
                replica[index].birthday = dayjs(e).format("YYYY-MM-DD");
                setChildInfo(replica);
              } else {
                setDueDate(dayjs(e).format("YYYY-MM-DD"));
              }
            }}
          />
        );
      },
    },
  ];

  useImperativeHandle(ref, () => ({
    validate: () => form.validateFields(),
    getFields: () => form.getFieldsValue(),
    reset: () => form.resetFields(),
    getDueDate: () => dueDate,
    getIsPregnant: () => isPregnant,
    getChildInfo: () => childInfo,
    getAddressInfo: () => addressInfo,
  }));

  useEffect(() => {
    if (clDl === "CL") setLeadType("");
    form.setFieldValue("media", "");
    const selectedList =
      clDl === "CL"
        ? mediaFullList.CL
        : leadType === "GD"
        ? mediaFullList.DLGD
        : leadType === "LD"
        ? mediaFullList.DLLD
        : mediaFullList.DLBD;
    setMediaList(selectedList);
  }, [clDl, leadType]);

  useEffect(() => {
    const loadDaumPostcode = () => {
      new window.daum.Postcode({
        oncomplete: function (data: any) {
          form.setFieldsValue({
            zip: data?.zonecode,
            address1: data?.address,
          });
          setAddressInfo({
            SGG_CD: data?.sigunguCode,
            ROAD_CD: data?.roadnameCode,
            EMD_CD: data?.bcode,
            BULD_NO: data?.buildingCode,
          });
          setShowAddressWindow(false);
        },
        width: "100%",
        height: "100%",
      }).embed(modalRef.current);
    };

    if (!window.daum || !window.daum.Postcode) {
      const script = document.createElement("script");
      script.src =
        "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      script.onload = loadDaumPostcode;
      document.body.appendChild(script);
    } else {
      loadDaumPostcode();
    }
  }, [showAddressWindow]);

  useEffect(() => {
    if (childInfo.length > 0) {
      if (
        !(
          (isPregnant && dayjs(dueDate).isValid()) ||
          (dayjs(childInfo[0].birthday).isValid() && childInfo[0].name)
        )
      ) {
        setChildInfoMissing(true);
      } else {
        setChildInfoMissing(false);
      }
    }
  }, [isPregnant, dueDate, childInfo]);

  useEffect(() => {
    if (advisors.length > 0) {
      setAdvisorsTreeSelectOptions(convertToAdvisorTreeSelectOptions(advisors));
    }
  }, [advisors]);

  useEffect(() => {
    fetchMedia();
    fetchAdvisorsData(header, setAdvisors);
  }, []);

  return (
    <div className="content-container">
      {/* <p className="text-center font-bold text-xl italic mb-5">
        This page is still under development, please don't perform any actions
        here
      </p>
      <p className="text-center font-bold text-xl italic mb-5">
        이 페이지는 아직 개발 중이니 작업을 수행하지 말아주세요.
      </p> */}
      <Form form={form} labelCol={{ span: 8 }} wrapperCol={{ span: 12 }}>
        <Form.Item label="리드유형" name="cldl" rules={[requiredRule]}>
          <Radio.Group
            options={[
              { label: "CL", value: "CL" },
              { label: "DL", value: "DL" },
            ]}
            optionType="button"
            onChange={(e) => {
              setClDl(e.target.value);
            }}
          />
        </Form.Item>
        {clDl === "DL" && (
          <Form.Item label="DL 유형" name="leadType" rules={[requiredRule]}>
            <Radio.Group
              options={[
                { label: "LD - 개인리드", value: "LD" },
                { label: "GD - 그룹리드 (지사활동리드)", value: "GD" },
                { label: "BD - 부스리드", value: "BD" },
              ]}
              onChange={(e) => setLeadType(e.target.value)}
            />
          </Form.Item>
        )}
        <Form.Item label="Media (리드유입)" name="media" rules={[requiredRule]}>
          <Select
            disabled={!clDl || (clDl === "DL" && !leadType)}
            placeholder="Media (리드유입)을 선택해주세요."
            options={mediaList?.map((i) => {
              return {
                label: i.code ? `${i.code}(${i.name})` : i.name,
                value:
                  clDl === "DL" ? i.categoryId || i.localMarketingId : i.name,
              };
            })}
          />
        </Form.Item>
        <Form.Item label="담당자" name="companyNumber" rules={[requiredRule]}>
          <TreeSelect
            treeData={advisorsTreeSelectOptions}
            placeholder="지사&담당자 선택"
            allowClear
          />
        </Form.Item>
        <Form.Item label="고객명" name="name" rules={[requiredRule]}>
          <Input placeholder="고객명을 입력해주세요." />
        </Form.Item>
        <Form.Item
          label="고객 휴대폰번호"
          name="phoneNumber"
          required
          rules={[
            {
              pattern: /^010-\d{4}-\d{4}$/,
              message:
                "휴대폰번호는 반드시 010-xxxx-xxxx 형식으로 입력해주세요.",
            },
          ]}
        >
          <Input placeholder="휴대폰번호를 입력해주세요." />
        </Form.Item>
        <Form.Item label="이메일" name="email">
          <Input placeholder="example@gmail.com" />
        </Form.Item>
        <Form.Item label="주소" required>
          <div className="flex justify-between mb-2 h-32px w-full">
            <Form.Item className="w-4/5" name="zip" rules={[requiredRule]}>
              <Input placeholder="우편번호" disabled />
            </Form.Item>
            <Form.Item className="ms-2">
              <Button
                icon={<SearchOutlined />}
                onClick={() => {
                  setShowAddressWindow(true);
                }}
              >
                주소찾기
              </Button>
            </Form.Item>
          </div>
          <Form.Item
            name="address1"
            className="mt-0 mb-2"
            rules={[requiredRule]}
          >
            <Input placeholder="주소" disabled />
          </Form.Item>
          <Form.Item name="address2" className="mt-0">
            <Input placeholder="상세주소" />
          </Form.Item>
        </Form.Item>
        <Form.Item label="자녀와의 관계" name="relation" rules={[requiredRule]}>
          <Select
            placeholder="자녀와의 관계를 선택해주세요."
            options={[
              { label: "엄마", value: "모" },
              { label: "아빠", value: "부" },
              { label: "그 외", value: "기타" },
            ]}
          />
        </Form.Item>
        <Form.Item
          label="자녀정보"
          name="childInfo"
          required
          rules={[
            {
              validator: (_, __) => {
                return childInfoMissing
                  ? Promise.reject(new Error("자녀정보를 입력해주세요."))
                  : Promise.resolve();
              },
            },
          ]}
        >
          <p className="reminder mt-2">
            임신 중이 아닐 경우 한 명 이상의 자녀 정보는 필수입니다.
          </p>
          <Table
            columns={columns}
            size="small"
            className={`mt-3 ${childInfoMissing ? "border-table" : ""}`}
            dataSource={childInfo}
            pagination={false}
          />
        </Form.Item>
        <Form.Item label="메모" name="memo" className="mt-4">
          <TextArea
            maxLength={100}
            showCount
            placeholder="추가사항을 메모하세요."
          />
        </Form.Item>
      </Form>
      {showAddressWindow && (
        <div className="address-modal">
          <div className="flex justify-between items-center title">
            <p>주소검색</p>
            <div
              onClick={() => {
                setShowAddressWindow(false);
              }}
            >
              <CloseOutlined />
            </div>
          </div>
          <div ref={modalRef} className="address-modal-2" />
        </div>
      )}
    </div>
  );
});

export default SingleLeadCreation;
